<template>
  <div class="add-creditmemo-body fade-in">
    <!-- GLOBAL LOADER -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading Credit Memo...</p>
      </div>
    </transition>

    <div v-if="!loading">
      <!-- HEADER -->
      <div class="section-header">
        <div class="title-area">
          <h2><i class="bx bx-receipt"></i> Create New Credit Memo</h2>
          <p>Issue a credit or refund against a client invoice.</p>
        </div>
        <!-- <button class="btn-help"><i class="bx bx-help-circle"></i> Help</button> -->
      </div>

      <div class="creditmemo-layout">
        <!-- LEFT MAIN AREA -->
        <div class="main-pane">
          <!-- CLIENT INFO -->
          <div class="card-section animate-in">
            <h3><i class="bx bx-user"></i> Client Information</h3>
            <div class="form-grid">
              <div class="form-group full">
                <label>Select Client</label>
                <div class="client-select" style="display: flex;">
                  <select v-model="form.client" style="width: 70%;">
                    <option value="">Choose Client</option>
                    <option v-for="client in clients" :key="client.user_id" :value="client.user_id">
                      {{ client.fname }} {{ client.lname }}
                    </option>
                  </select>
                  &nbsp;&nbsp;&nbsp;
                  <button class="btn-outline" @click="showAddClientModal = true"  style="width: 30%;">
                    <i class="bx bx-plus"></i> Add New
                  </button>
                </div>
              </div>
            </div>

            <transition name="fade-slide">
              <div v-if="selectedClient" class="client-preview" style="margin-top: 1rem;">
                <p><b>Email:</b> {{ selectedClient.email }}</p>
                <p><b>Phone:</b> {{ selectedClient.mobile }}</p>
                <p><b>Address:</b> {{ selectedClient.address_line1 }}</p>
              </div>
            </transition>
          </div>

          <!-- CREDIT MEMO DETAILS -->
          <div class="card-section animate-in">
            <h3><i class="bx bx-file"></i> Credit Memo Details</h3>
            <div class="form-grid two-col">
              <div class="form-group">
                <label>Credit Memo No.</label>
                <input v-model="form.creditRef" readonly />
              </div>
              <div class="form-group">
                <label>Date</label>
                <input type="date" v-model="form.date" />
              </div>
              <div class="form-group">
                <label>Reference Invoice</label>
                <select v-model="form.referenceInvoice">
                  <option value="">Select Invoice</option>
                  <option v-for="inv in filteredInvoices" :key="inv.id" :value="inv.id">{{ inv.refNumber }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>Status</label>
                <select v-model="form.status">
                  <option>Draft</option>
                  <option>Issued</option>
                  <option>Applied</option>
                  <option>Voided</option>
                </select>
              </div>
            </div>

            <div class="form-group full" style="margin-top: 1rem;">
              <label>Reason for Credit</label>
              <input v-model="form.reason" placeholder="Enter reason (e.g., Product return, Overcharge)" />
            </div>
          </div>

          <!-- LINE ITEMS -->
          <div class="card-section animate-in">
            <div class="section-header-mini">
              <h3><i class="bx bx-list-plus"></i> Credited Items</h3>
              <button class="btn-black-sm" @click="addItemNow"><i class="bx bx-plus"></i> Add Item</button>
            </div>

            <transition-group name="fade-slide" tag="div" style="margin-top: 1rem;">
              <div v-for="(item, i) in form.items" :key="i" class="item-row">
                <div class="custom-select">
                  <select v-model="item.name" @change="handleItemSelect($event, i)">
                    <option value="">Select Product</option>
                    <option v-for="p in products" :key="p.item_id" :value="p.item_name">
                      {{ p.item_name }}
                    </option>
                  </select>
                </div>
                <input v-model.number="item.qty" type="number" min="1" placeholder="Qty" />
                <input v-model.number="item.rate" type="number" min="0" placeholder="Rate ($)" />
                <span class="amount text-negative">-${{ (item.qty * item.rate).toFixed(2) }}</span>
                <button class="btn-remove" @click="removeItem(i)">
                  <i class="bx bx-x"></i>
                </button>
              </div>
            </transition-group>

            <div class="add-product-row">
              <button class="btn-outline-sm" @click="showAddProductModal = true">
                <i class="bx bx-plus-circle"></i> Add New Product
              </button>
            </div>
          </div>

          <!-- SUMMARY -->
          <div class="card-section summary animate-in credit-summary">
            <div class="summary-row"><span>Subtotal</span><strong>-${{ subtotal.toFixed(2) }}</strong></div>
            <div class="summary-row"><span>Tax (10%)</span><strong>-${{ tax.toFixed(2) }}</strong></div>
            <div class="summary-row total">
              <span>Total Credit</span>
              <strong class="text-negative">-${{ total.toFixed(2) }}</strong>
            </div>
          </div>

          <!-- NOTES -->
          <div class="card-section animate-in">
            <h3><i class="bx bx-edit"></i> Notes</h3>
            <textarea v-model="form.notes" placeholder="Add any comments for this credit memo..." rows="3"></textarea>
          </div>
        </div>

        <!-- RIGHT SIDEBAR -->
        <div class="side-pane">
          <div class="card-section sticky animate-in">
            <button class="btn-primary full" @click="submitCreditMemo('Sent')"><i class="bx bx-send"></i> Send Credit Memo</button>
            <button class="btn-outline full" style="margin-top: 1rem;" @click="openPreview"><i class="bx bx-show"></i> Preview</button>
            <button class="btn-outline full"  @click="submitCreditMemo('Draft')" style="margin-top: 1rem;"><i class="bx bx-save"></i> Save Draft</button>
            <button class="btn-black full"  @click="submitCreditMemo('Approved')" style="margin-top: 1rem;"><i class="bx bx-transfer-alt"></i> Apply to Invoice</button>

            <button class="btn-outline full mt-4" style="margin-top: 1rem;" @click="openSettingsDrawer">
              <i class="bx bx-cog"></i> Settings
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ADD PRODUCT MODAL -->
    <transition name="fade-slide">
      <div v-if="showAddProductModal" class="modal-overlay" @click.self="showAddProductModal = false">
        <div class="modal-card">
          <h3><i class="bx bx-box"></i> Add New Product</h3>
          <div class="form-group"><label>Product Name</label><input v-model="newProduct.item_name" /></div>
          <div class="form-group"><label>Rate ($)</label><input v-model.number="newProduct.item_price" type="number" /></div>
          <div class="modal-actions">
            <button class="btn-primary" @click="addNewProduct">Add</button>
            <button class="btn-outline" @click="showAddProductModal = false">Cancel</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ============================
     FULLSCREEN PREVIEW POPUP 
=============================== -->
<transition name="fade">
  <div v-if="showPreview" class="preview-fullscreen" @click.self="closePreview">
    <div class="preview-content">
      <button class="preview-close-btn" @click="closePreview">✕</button>

      <UniversalPreviewPrint
        :record="previewRecord"
        :branding="previewBranding"
        mainType="creditmemo"
      />
    </div>
  </div>
</transition>

    <!-- ADD CLIENT MODAL -->
    <transition name="fade-slide">
      <div v-if="showAddClientModal" class="modal-overlay" @click.self="showAddClientModal = false">
        <div class="modal-card">
          <h3><i class="bx bx-user-plus"></i> Add New Client</h3>
          <div class="form-group"><label>First Name</label><input v-model="newClient.fname" /></div>
          <div class="form-group"><label>Last Name</label><input v-model="newClient.lname" /></div>
          <div class="form-group"><label>Email</label><input v-model="newClient.email" /></div>
          <div class="form-group"><label>Mobile</label><input v-model="newClient.mobile" /></div>
          <div class="form-group"><label>Address</label><textarea v-model="newClient.address"></textarea></div>
          <div class="modal-actions">
            <button class="btn-primary" @click="addNewClient">Add</button>
            <button class="btn-outline" @click="showAddClientModal = false">Cancel</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- SETTINGS DRAWER -->
    <transition name="slide-drawer">
      <div v-if="showSettings" class="settings-drawer">
        <div class="drawer-header">
          <h3><i class="bx bx-cog"></i> Credit Memo Settings</h3>
          <button @click="closeSettingsDrawer" class="close-btn"><i class="bx bx-x"></i></button>
        </div>
        <div class="drawer-content">
          <section>
            <h4>Auto Apply to Invoices</h4>
            <p>Automatically apply credits to open invoices.</p>
            <label class="switch">
              <input type="checkbox" v-model="autoApply" />
              <span class="slider"></span>
            </label>
          </section>

          

          <section class="branding-settings">
            <h4><i class="bx bx-palette"></i> Customize Template</h4>
            <div class="branding-grid">
              <div class="upload-card" @click="triggerUpload">
                <input type="file" ref="fileInput" @change="handleFile" style="display: none" />
                <template v-if="!form.image">
                  <div class="upload-icon">
                    <i class="bx bx-image-add"></i>
                  </div>
                  <p>Upload Logo</p>
                </template>
                <img v-else :src="apiBaseUrl+form.image" alt="Logo" />
              </div>

              <div class="color-card">
                <label>Theme Color</label>
                <div class="color-picker">
                  <div class="color-preview" :style="{ backgroundColor: form.color }"></div>
                  <input type="color" v-model="form.color" />
                  <span class="color-code">{{ form.color.toUpperCase() }}</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </transition>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { useToast } from "vue-toastification";
import { createInvoice, getInvoiceById, getTableRowCount,getInvoices } from "@/apis/invoiceapis";
import { getItems, addItem } from "@/apis/itemapis";
import { getClients, addClient } from "@/apis/clientapis";
import UniversalPreviewPrint from "@/comps/UniversalPreviewPrint.vue";
import { apiBaseUrl } from "@/apis/appconst";

const showPreview = ref(false);
const previewRecord = ref({});
const previewBranding = ref({});


const toast = useToast();
const loading = ref(true);
const fileInput = ref(null);

// ===== FORM STATE =====
const form = ref({
  id: "",
  client: "",
  creditRef: "",
  referenceInvoice: "",
  date: "",
  status: "Draft",
  reason: "",
  notes: "",
  image: "",
  color: "#111827",
  items: [{ name: "", qty: 1, rate: 0 }],
  globalTaxType: "none",
          globalTaxValue: parseFloat(0),

          globalDiscountType: "none",
          globalDiscountValue: parseFloat(0),

          otherAmount: parseFloat(0),
          finalTotal: parseFloat(0),
});

// ===== BASIC REFS =====
const clients = ref([]);
const products = ref([]);
const invoices = ref([]);
const showAddProductModal = ref(false);
const showAddClientModal = ref(false);

// ===== NEW PRODUCT & CLIENT =====
const newProduct = ref({ item_name: "", item_price: 0 });
const newClient = ref({
  fname: "",
  lname: "",
  email: "",
  mobile: "",
  address: "",
});

// ===== SETTINGS =====
const showSettings = ref(false);
const autoApply = ref(true);

function openPreview() {
  previewRecord.value = {
    number: form.value.creditRef || "",
    date: form.value.date || "",
    due_date: "",   // Credit memo has no due date

    client_name:
      (selectedClient.value?.fname || "") +
      " " +
      (selectedClient.value?.lname || ""),

    client_email: selectedClient.value?.email || "",
    client_mobile: selectedClient.value?.mobile || "",
    client_address: selectedClient.value?.address_line1 || "",

    subtotal: subtotal.value,
    discount_total: Number(form.value.globalDiscountValue || 0),
    tax_total: Number(form.value.globalTaxValue || 0),
    other_amount: Number(form.value.otherAmount || 0),
    total: total.value,

    notes: form.value.notes || "",
    reason: form.value.reason || "",

    items: form.value.items.map((it) => {
      const qty = Number(it.qty || 0);
      const rate = Number(it.rate || 0);

      const base = qty * rate;

      return {
        name: it.name || "",
        qty,
        rate,
        base_amount: base,
        discount_amount: 0,
        tax_amount: 0,
        final_total: base * -1, // ✔ Credit memo reduces amount
      };
    }),
  };

  previewBranding.value = {
    logo: apiBaseUrl+form.value.image || "",
    title: "CREDIT MEMO",
    email: "",
    phone: "",
    address: "",
    primary: form.value.color || "#111827",
    secondary: "#6b7280",
  };

  showPreview.value = true;
}

function closePreview() {
  showPreview.value = false;
}


// ===== COMPUTED =====
const selectedClient = computed(() =>
  clients.value.find((c) => c.user_id === form.value.client)

);
const filteredInvoices = computed(() => {
  return invoices.value;
    // .filter((inv) => inv.clid === form.value.client)
    // .map((inv) => ({
    //   id: inv.id,
    //   refNumber: inv.refNumber,
    // }));
});



const subtotal = computed(() =>
  form.value.items.reduce((sum, i) => sum + i.qty * i.rate, 0)
);
const tax = computed(() => subtotal.value * 0.1);
const total = computed(() => subtotal.value + tax.value);

// ===== FUNCTIONS =====
const addItemNow = () => form.value.items.push({ name: "", qty: 1, rate: 0 });
const removeItem = (i) => form.value.items.splice(i, 1);

const handleItemSelect = (e, i) => {
  const sel = products.value.find((p) => p.item_name === e.target.value);
  if (sel) form.value.items[i].rate = parseFloat(sel.item_unit_price || sel.item_price || 0);
};

const triggerUpload = () => fileInput.value.click();

const handleFile = (event) => {
  const file = event.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => (form.value.image = e.target.result);
    reader.readAsDataURL(file);
  }
};

// ===== LOADERS =====
async function loadClients() {
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await getClients(userData.user_id + "", "", 0, "");
  if (res?.status) clients.value = res.records;
}

async function loadProducts() {
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await getItems(0, userData.user_id + "", "");
  if (res?.status) products.value = res.records;
}

// ===== ADD NEW PRODUCT / CLIENT =====
const addNewProduct = async () => {
  if (!newProduct.value.item_name.trim()) return toast.warning("Enter product name");
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await addItem({
    item_name: newProduct.value.item_name,
    item_price: newProduct.value.item_price,
    item_by: userData.user_id,
  });
  if (res?.status) {
    toast.success("Product added");
    await loadProducts();
    showAddProductModal.value = false;
  }
};

const addNewClient = async () => {
  if (!newClient.value.fname || !newClient.value.email)
    return toast.warning("First name & email required");
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await addClient({ ...newClient.value, user_cre_by: userData.user_id });
  if (res?.status) {
    toast.success("Client added");
    await loadClients();
    showAddClientModal.value = false;
  }
};

// ===== SUBMIT CREDIT MEMO =====
const submitCreditMemo = async (status1) => {
  try {
 if (!form.value.client) {
      toast.warning("Please select a client.");
      return;
    }
    if (!form.value.creditRef?.trim()) {
      toast.warning("Credit memo number is missing.");
      return;
    }
    if (!form.value.date) {
      toast.warning("Please select a valid credit memo date.");
      return;
    }
    if (!form.value.referenceInvoice) {
      toast.warning("Please select a reference invoice.");
      return;
    }
    if (!form.value.reason?.trim()) {
      toast.warning("Please provide a reason for this credit memo.");
      return;
    }

    if (!form.value.items.length) {
      toast.warning("Please add at least one credited item.");
      return;
    }

    for (const [index, item] of form.value.items.entries()) {
      if (!item.name?.trim()) {
        toast.warning(`Item ${index + 1}: Name is required.`);
        return;
      }
      if (!item.qty || item.qty <= 0) {
        toast.warning(`Item ${index + 1}: Quantity must be greater than 0.`);
        return;
      }
      if (isNaN(item.rate) || item.rate < 0) {
        toast.warning(`Item ${index + 1}: Rate must be a valid positive number.`);
        return;
      }
    }

    if (total.value <= 0) {
      toast.warning("Total credit amount must be greater than zero.");
      return;
    }

    loading.value = true;
    toast.success("Processing Credit Memo...");

    loading.value = true;
    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};

    const formattedItems = form.value.items.map((it) => ({
      name: it.name,
      qty: it.qty,
      rate: it.rate,
    }));

    const payload = {
      credit_id: form.value.id,
      invoice_number: form.value.creditRef,
      invoice_client_id: form.value.client,
      invoice_ref: form.value.referenceInvoice,
      invoice_date: form.value.date,
      invoice_status: status1 + "",
      invoice_notes: form.value.notes,
      invoice_theme_color: form.value.color,
      invoice_logo: form.value.image,

      // 🔥 NEW CORRECT CALC FIELDS
      invoice_subtotal: subtotal.value,
      invoice_global_tax_type: form.value.globalTaxType,
      invoice_global_tax_value: form.value.globalTaxValue,
      invoice_global_discount_type: form.value.globalDiscountType,
      invoice_global_discount_value: form.value.globalDiscountValue,
      invoice_other_amount: form.value.otherAmount,
      invoice_tax_amount: tax.value,
      invoice_total_amount: total.value,
      invoice_final_total: total.value,

      invoice_reason: form.value.reason,
      invoice_created_by: userData.user_id,

      items: formattedItems,
    };


    const res = await createInvoice("creditmemo", payload);
    if (res?.status) {
      toast.success("Credit Memo saved successfully!");
      window.location.href = "/invoices?type=creditmemo";
    } else {
      toast.error(res?.message || "Failed to save Credit Memo");
    }
  } catch (err) {
    console.error("Submit Credit Memo Error:", err);
    toast.error("Unexpected error occurred.");
  } finally {
    loading.value = false;
  }
};
const loadInvoices = async () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
     // as per your instruction

    const res = await getInvoices("invoice", userData.user_id + "", "none", "", "", "");
    if (res?.status && res.records?.length) {
      invoices.value = res.records.map((inv) => ({
        id: inv.invoice_id,
        refNumber: inv.invoice_number,
        clid: inv.invoice_client_id,
      }));
    } else {
      invoices.value = [];
      toast.success("No invoices found for reference.");
    }
  } catch (err) {
    console.error("loadInvoices error:", err);
    toast.error("Failed to load invoices.");
  }
};


// ===== INITIAL LOAD =====
onMounted(async () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const memoId = urlParams.get("id");

    await Promise.all([loadClients(), loadProducts(),loadInvoices()]);

    if (memoId) {
      const res = await getInvoiceById("creditmemo", memoId);
      if (res?.status && res.record) {
        const cm = res.record;

        form.value = {
          id: memoId,
          creditRef: cm.credit_number || "",
          client: cm.credit_client_id || "",
          referenceInvoice: cm.credit_ref_invoice_id || "",
          date: cm.credit_date || "",
          status: cm.credit_status || "Draft",
          reason: cm.credit_reason || "",
          notes: cm.credit_notes || "",
          image: apiBaseUrl+cm.credit_logo || "",
          color: cm.credit_theme_color || "#111827",

          // 🔥 NEW GLOBAL CALC FIELDS (Correct Mapping)
          globalTaxType: cm.credit_global_tax_type || "none",
          globalTaxValue: parseFloat(cm.credit_global_tax_value || 0),

          globalDiscountType: cm.credit_global_discount_type || "none",
          globalDiscountValue: parseFloat(cm.credit_global_discount_value || 0),

          otherAmount: parseFloat(cm.credit_other_amount || 0),
          finalTotal: parseFloat(cm.credit_final_total || 0),

          // 🔥 LINE ITEMS
          items:
            cm.items?.map((it) => ({
              name: it.credit_item_name,
              qty: parseFloat(it.credit_item_qty || 1),
              rate: parseFloat(it.credit_item_rate || 0),
            })) || [{ name: "", qty: 1, rate: 0 }],
        };

      }
    } else {
      const countRes = await getTableRowCount("creditmemo");
      if (countRes.status)
        form.value.creditRef = `CM-${String(countRes.total_rows + 1).padStart(4, "0")}`;
    }
  } catch (err) {
    console.error("Init Credit Memo Error:", err);
  } finally {
    await nextTick();
    loading.value = false;
  }
});

// ===== SETTINGS DRAWER =====
const openSettingsDrawer = () => (showSettings.value = true);
const closeSettingsDrawer = () => (showSettings.value = false);
</script>


<style scoped>
/* === ADD ITEM BUTTON (Top of Line Items Section) === */
.btn-black-sm {
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 1.1rem;
  font-size: 0.9rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.btn-black-sm i {
  font-size: 1rem;
  transition: transform 0.25s ease;
}

.btn-black-sm:hover {
  background: #1e293b;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(17, 24, 39, 0.2);
}

.btn-black-sm:hover i {
  transform: translateX(3px);
}

.btn-black-sm:active {
  transform: scale(0.96);
}

/* === MOBILE ALIGNMENT FIX === */
@media (max-width: 768px) {
  .section-header-mini {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.6rem;
  }

  .btn-black-sm {
    width: 100%;
    justify-content: center;
    font-size: 0.9rem;
    padding: 0.7rem;
  }

  .btn-black-sm + * {
    margin-top: 0.6rem;
  }
}

/* === LOADER === */
.global-loader {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(3px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loader-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #ddd;
  border-top: 4px solid #111827;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* === MICRO UX POLISH === */
.toastify {
  font-family: "Inter", sans-serif;
  font-size: 0.9rem;
}

/* ===========================
   MAIN CONTAINER
=========================== */
.add-creditmemo-body {
  background: #fff;
  border-radius: 1.5rem;
  padding: 2rem 2.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  font-family: "Inter", sans-serif;
}

/* ===========================
   HEADER AREA
=========================== */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 1.9rem;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.section-header p {
  color: #6b7280;
  margin-top: 0.3rem;
  font-size: 0.95rem;
}

.btn-help {
  background: #111827;
  color: #fff;
  border-radius: 8px;
  padding: 0.7rem 1.3rem;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}
.btn-help:hover {
  background: #374151;
  transform: translateY(-1px);
}

/* ===========================
   LAYOUT GRID
=========================== */
.creditmemo-layout {
  display: flex;
  gap: 2rem;
}
.main-pane {
  width: 68%;
}
.side-pane {
  width: 32%;
}

/* ===========================
   CARD SECTIONS
=========================== */
.card-section {
  background: #f9fafb;
  border-radius: 1rem;
  padding: 1.8rem;
  margin-bottom: 1.5rem;
  transition: box-shadow 0.3s ease;
}
.card-section:hover {
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);
}

.card-section h3 {
  font-size: 1.1rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

/* ===========================
   FORM GRID
=========================== */
.form-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.form-grid.two-col .form-group {
  flex: 1 1 calc(50% - 1rem);
}
.form-group.full {
  flex: 1 1 100%;
}

label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
  margin-bottom: 0.3rem;
  display: block;
}

input,
select,
textarea {
  width: 100%;
  padding: 0.8rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: #fff;
}
input:focus,
select:focus,
textarea:focus {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
  outline: none;
}
textarea {
  resize: none;
}

/* ===========================
   ITEM ROWS
=========================== */
.item-row {
  display: grid;
  grid-template-columns: 2fr 0.8fr 0.8fr 1fr 40px;
  gap: 0.8rem;
  align-items: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.8rem;
  margin-bottom: 0.8rem;
  transition: all 0.3s ease;
}
.item-row:hover {
  border-color: #111827;
  transform: scale(1.01);
}

.amount {
  text-align: right;
  font-weight: 600;
  color: #111827;
}
.text-negative {
  color: #b91c1c !important;
}

/* Remove Button */
.btn-remove {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 1.3rem;
  cursor: pointer;
  transition: transform 0.25s ease, color 0.25s ease;
}
.btn-remove:hover {
  color: #dc2626;
  transform: rotate(90deg);
}

/* ===========================
   ADD PRODUCT
=========================== */
.add-product-row {
  margin-top: 1rem;
  text-align: right;
}
.btn-outline-sm {
  background: #fff;
  color: #111827;
  border: 1px solid #111827;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
}
.btn-outline-sm:hover {
  background: #111827;
  color: #fff;
  transform: translateY(-1px);
}

/* ===========================
   SUMMARY SECTION
=========================== */
.summary {
  background: #111827;
  color: #fff;
}
.credit-summary {
  border-left: 4px solid #b91c1c;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.4rem;
}
.summary-row.total {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 0.8rem;
  padding-top: 0.8rem;
}

/* ===========================
   BUTTON STYLES
=========================== */
.btn-primary,
.btn-black,
.btn-outline {
  width: 100%;  
  transition: all 0.3s ease;
  font-weight: 500;
}
.btn-primary {
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1.4rem;
}
.btn-primary:hover {
  background: #1f2937;
  transform: translateY(-1px);
}
.btn-black {
  background: #111827;
  color: #fff;
  border-radius: 10px;
  border: none;
  padding: 0.8rem 1.4rem;
}
.btn-outline {
  background: #fff;
  border: 1px solid #111827;
  color: #111827;
  border-radius: 10px;
  padding: 0.7rem 1.4rem;
}
.btn-outline:hover {
  background: #111827;
  color: #fff;
}

/* ===========================
   MODAL (ADD PRODUCT)
=========================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}
.modal-card {
  background: #fff;
  padding: 2rem;
  border-radius: 16px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  animation: scaleIn 0.3s ease;
}
.modal-card h3 {
  font-size: 1.2rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.modal-card .form-group {
  margin-bottom: 1rem;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
}

/* ===========================
   SETTINGS DRAWER
=========================== */
.settings-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: #fff;
  box-shadow: -6px 0 20px rgba(0, 0, 0, 0.1);
  border-radius: 1rem 0 0 1rem;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 2rem;
  z-index: 1000;
  overflow-y: auto;
}
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}
.drawer-header h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.close-btn {
  border: none;
  background: none;
  font-size: 1.6rem;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.25s ease;
}
.close-btn:hover {
  color: #111827;
}
.drawer-content section {
  margin-bottom: 1.8rem;
}
.drawer-content h4 {
  margin-bottom: 0.4rem;
  color: #111827;
  font-weight: 600;
}
.drawer-content p {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.8rem;
}

/* Toggle Switch */
.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #e5e7eb;
  border-radius: 34px;
  transition: 0.3s;
}
.slider::before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
}
input:checked + .slider {
  background: #111827;
}
input:checked + .slider::before {
  transform: translateX(22px);
}

/* ===========================
   ANIMATIONS
=========================== */
.fade-in {
  animation: fadeIn 0.6s ease;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes scaleIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
.fade-slide-enter-active {
  transition: all 0.3s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.slide-drawer-enter-active,
.slide-drawer-leave-active {
  transition: all 0.4s ease;
}
.slide-drawer-enter-from,
.slide-drawer-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* ===========================
   RESPONSIVE DESIGN
=========================== */
@media (max-width: 992px) {
  .creditmemo-layout {
    flex-direction: column;
  }
  .main-pane,
  .side-pane {
    width: 100%;
  }
  .settings-drawer {
    width: 100%;
    border-radius: 1rem 1rem 0 0;
  }
}

@media (max-width: 600px) {
  .item-row {
    grid-template-columns: 1fr 1fr;
  }
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}
/* === FIXED ALIGNMENTS & SPACING === */
.section-header .title-area {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.card-section .form-group {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.section-header-mini {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
}

/* Make input fields uniform in height */
input,
select,
textarea {
  line-height: 1.4;
  height: auto;
}

/* === BUTTON ALIGNMENT FIX === */
.btn-primary.full,
.btn-outline.full,
.btn-black.full {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
}

/* Make icons vertically centered */
.btn-primary i,
.btn-outline i,
.btn-black i {
  font-size: 1.1rem;
  position: relative;
  top: 0.5px;
}

/* === FORM FIELD SPACING === */
.form-group + .form-group {
  margin-top: 0.8rem;
}

/* === MODAL CENTERING FIX === */
.modal-card {
  display: flex;
  flex-direction: column;
}
.modal-actions button {
  min-width: 110px;
}
.modal-actions .btn-outline:hover {
  background: #111827;
  color: #fff;
}

/* === SCROLLABLE SETTINGS DRAWER === */
.settings-drawer::-webkit-scrollbar {
  width: 6px;
}
.settings-drawer::-webkit-scrollbar-thumb {
  background: #9ca3af;
  border-radius: 4px;
}
.settings-drawer::-webkit-scrollbar-track {
  background: #f3f4f6;
}

/* === IMPROVE FIELD ALIGNMENT IN SMALL SCREENS === */
@media (max-width: 768px) {
  .form-grid.two-col .form-group {
    flex: 1 1 100%;
  }

  .section-header h2 {
    font-size: 1.5rem;
  }

  .item-row {
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }

  .amount {
    text-align: left;
  }

  .add-product-row {
    text-align: left;
  }
}

/* === CREDIT MEMO HIGHLIGHT COLOR === */
.summary.credit-summary {
  background: #111827;
  color: #fff;
}
.summary.credit-summary .summary-row.total strong {
  color: #fca5a5;
}

/* === POLISH MICRO INTERACTIONS === */
button,
select,
input,
textarea {
  transition: all 0.25s ease;
}

button:active {
  transform: scale(0.97);
}

.card-section {
  transition: transform 0.2s ease, box-shadow 0.3s ease;
}
.card-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(17, 24, 39, 0.08);
}
/* === GENERAL CONTAINER POLISH === */
.add-invoice-body {
  max-width: 1280px;
  margin: 0 auto;
  overflow-x: hidden;
}

/* Ensure smooth animations when switching between states */
.fade-in,
.animate-in {
  animation: fadeIn 0.4s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === CARD SHADOW AND DEPTH === */
.card-section {
  transition: box-shadow 0.3s ease, transform 0.2s ease;
}
.card-section:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-3px);
}

/* === FORM GRID BALANCE === */
.form-grid {
  align-items: flex-start;
}
.form-group input,
.form-group select,
.form-group textarea {
  min-height: 44px;
}

/* === LINE ITEM ENHANCEMENTS === */
.item-row select {
  border-radius: 8px;
  background-color: #fff;
}
.item-row input {
  text-align: center;
}
.item-row .amount {
  font-weight: 700;
  color: #111827;
}

/* Add subtle separation between items */
.item-row:not(:last-child) {
  border-bottom: 1px solid #f3f4f6;
}

/* === ADD PRODUCT MODAL POLISH === */
.modal-card {
  position: relative;
  animation: scaleIn 0.25s ease;
}
@keyframes scaleIn {
  from {
    transform: scale(0.97);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
.modal-card h3 i {
  color: var(--primary, #111827);
}
.modal-actions button {
  border-radius: 10px;
  font-weight: 600;
}

/* === SETTINGS DRAWER FIXES === */
.drawer-content {
  overflow-y: auto;
  flex: 1;
  padding-right: 0.3rem;
}

.drawer-content section p {
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.4;
  margin-bottom: 0.6rem;
}

/* === CREDIT MEMO BADGE COLORS === */
.summary.credit-summary {
  background: linear-gradient(135deg, #111827, #111827);
  color: #fff;
  border: none;
}
.summary.credit-summary .summary-row {
  color: #fef2f2;
}
.summary.credit-summary .summary-row.total strong {
  color: #fca5a5;
}

/* === BUTTON INTERACTIONS === */
.btn-outline-sm i,
.btn-outline i {
  transition: transform 0.3s ease;
}
.btn-outline-sm:hover i,
.btn-outline:hover i {
  transform: translateX(3px);
}

/* === ALIGNMENTS FOR SIDE BUTTONS === */
.side-pane .card-section button {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

/* === DIVIDER FIX === */
.divider span {
  background: #fff;
  padding: 0 8px;
  position: relative;
  z-index: 1;
  font-size: 13px;
  color: #6b7280;
}

/* === RESPONSIVE === */
@media (max-width: 992px) {
  .add-invoice-body {
    padding: 1.5rem;
  }

  .card-section {
    padding: 1.2rem;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.6rem;
  }

  .item-row {
    grid-template-columns: 1fr 0.8fr 0.8fr;
  }

  .amount {
    text-align: left;
  }

  .summary {
    border-radius: 1rem;
    padding: 1.5rem;
  }

  .settings-drawer {
    width: 100%;
    border-radius: 1rem 1rem 0 0;
  }
}

/* === HOVER FEEDBACK === */
.btn-primary:hover,
.btn-black:hover {
  background: #1f2937;
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.2);
}

/* === MICRO INTERACTIONS === */
button:active,
.btn-outline:active {
  transform: scale(0.98);
}
.branding-settings {
  margin-top: 1.2rem;
}

.branding-settings h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #111827;
  font-size: 1rem;
  margin-bottom: 1rem;
}

.branding-grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 1rem;
  align-items: center;
}

.upload-card {
  width: 110px;
  height: 110px;
  border-radius: 12px;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.upload-card:hover {
  border-color: var(--primary-color, #111827);
  background: #f3f4f6;
  transform: translateY(-2px);
}

.upload-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.upload-card:hover img {
  transform: scale(1.05);
}

.upload-card .upload-icon i {
  font-size: 1.6rem;
  color: #6b7280;
}

.upload-card p {
  font-size: 0.8rem;
  color: #9ca3af;
  margin-top: 4px;
}

.color-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.8rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.color-picker {
  display: flex;
  align-items: center;
  gap: 10px;
}

.color-preview {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  box-shadow: inset 0 0 3px rgba(0, 0, 0, 0.05);
}

.color-picker input[type="color"] {
  border: none;
  width: 40px;
  height: 30px;
  cursor: pointer;
  background: transparent;
  padding: 0;
}

.color-code {
  font-size: 0.8rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 6px;
}
/* === 📱 MOBILE RESPONSIVE PATCH === */
@media (max-width: 768px) {
  /* --- Main Container --- */
  .add-creditmemo-body {
    padding: 1.2rem !important;
    border-radius: 1rem !important;
  }

  /* --- Header --- */
  .section-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  .section-header h2 {
    font-size: 1.4rem !important;
  }

  .section-header p {
    font-size: 0.9rem !important;
  }

  .btn-help {
    width: 100% !important;
    justify-content: center !important;
    font-size: 0.9rem !important;
    padding: 0.6rem 1rem !important;
  }

  /* --- Layout --- */
  .creditmemo-layout {
    flex-direction: column !important;
    gap: 1.2rem !important;
  }

  .main-pane,
  .side-pane {
    width: 100% !important;
  }

  /* --- Cards --- */
  .card-section {
    padding: 1.2rem !important;
    border-radius: 0.8rem !important;
  }

  /* --- Form Fields --- */
  .form-grid.two-col .form-group {
    flex: 1 1 100% !important;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    font-size: 0.9rem !important;
    padding: 0.7rem 0.9rem !important;
  }

  /* --- Line Items --- */
  .item-row {
    grid-template-columns: 1fr 1fr !important;
    gap: 0.6rem !important;
    padding: 0.8rem !important;
  }

  .item-row select,
  .item-row input {
    font-size: 0.9rem !important;
  }

  .item-row .amount {
    text-align: left !important;
    font-size: 0.9rem !important;
  }

  .btn-remove {
    justify-self: flex-end !important;
    font-size: 1rem !important;
  }

  /* --- Add Product Button --- */
  .add-product-row {
    justify-content: center !important;
    text-align: center !important;
  }

  .btn-outline-sm {
    width: 100% !important;
  }

  /* --- Summary --- */
  .summary {
    padding: 1.2rem !important;
    border-radius: 0.8rem !important;
  }

  .summary-row {
    font-size: 0.85rem !important;
  }

  .summary-row.total strong {
    font-size: 1rem !important;
  }

  /* --- Notes --- */
  textarea {
    font-size: 0.9rem !important;
    padding: 0.7rem !important;
  }

  /* --- Buttons --- */
  .btn-primary,
  .btn-outline,
  .btn-black {
    width: 100% !important;
    justify-content: center !important;
    font-size: 0.95rem !important;
    padding: 0.8rem !important;
  }

  .side-pane .card-section button {
    margin-top: 0.6rem !important;
  }

  /* --- Settings Drawer --- */
  .settings-drawer {
    width: 100% !important;
    height: 90vh !important;
    border-radius: 1rem 1rem 0 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    padding: 1.4rem !important;
  }

  .settings-drawer::-webkit-scrollbar {
    width: 6px;
  }

  .settings-drawer::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
  }

  /* --- Branding Section --- */
  .branding-grid {
    grid-template-columns: 1fr !important;
    gap: 1rem !important;
  }

  .upload-card {
    margin: 0 auto !important;
  }

  /* --- Drawer Sections --- */
  .drawer-content section {
    padding: 1rem !important;
  }

  /* --- Credit Summary --- */
  .summary.credit-summary {
    border-left: 4px solid #b91c1c !important;
    background: #111827 !important;
    color: #fff !important;
  }

  .summary.credit-summary .summary-row.total strong {
    color: #fca5a5 !important;
  }
}

/* === 📱 Small Devices (≤480px) === */
@media (max-width: 480px) {
  .section-header h2 {
    font-size: 1.2rem !important;
  }

  .card-section {
    padding: 1rem !important;
  }

  .item-row {
    grid-template-columns: 1fr !important;
    gap: 0.5rem !important;
  }

  .item-row input,
  .item-row select {
    width: 100% !important;
  }

  .summary {
    padding: 1rem !important;
  }

  .btn-help {
    width: 100% !important;
    text-align: center !important;
  }

  .settings-drawer {
    height: 85vh !important;
  }
}
.preview-fullscreen {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 4000;
  backdrop-filter: blur(4px);
  padding: 20px;
}

.preview-content {
  background: white;
  width: 100%;
  max-width: 1000px;
  max-height: 95vh;
  border-radius: 12px;
  overflow-y: auto;
  padding: 20px;
  position: relative;
  animation: zoomIn 0.25s ease;
}

.preview-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #111827;
  color: white;
  font-size: 1.4rem;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: 0.25s;
}

.preview-close-btn:hover {
  transform: scale(1.1);
  background: #000;
}

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .25s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>

